import { Component, Input, OnDestroy, OnInit, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { RouterLink } from '@angular/router';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { AuthorizationService } from '@nimmsta/ng-commons-lib/auth';
import { LoadingIndicatorComponent } from '@nimmsta/ng-commons-lib/loading-indicator';
import { HttpErrorResponse } from '@angular/common/http';
import dayjs, { Dayjs } from 'dayjs';
import { DashboardComponentInterface } from '../../../../models/dashboard';
import {
  AveragePickTimeDashboardService,
  JobAveragePickTimeResponse,
} from './average-pick-time-dashboard.service';
import {
  AveragePickTimeDashboardConfiguration,
  UnitSourceType,
} from './average-pick-time-dashboard-configuration';
import { JobDashboardRange } from '../job-dashboard/job-dashboard-configuration';

@Component({
  selector: 'app-average-pick-time-dashboard',
  templateUrl: './average-pick-time-dashboard.component.html',
  styleUrls: ['./average-pick-time-dashboard.component.scss'],
  imports: [CommonModule, RouterLink, LoadingIndicatorComponent, TranslatePipe],
})
export class AveragePickTimeDashboardComponent
  implements OnInit, OnDestroy, DashboardComponentInterface
{
  private authorizationService = inject(AuthorizationService);
  private service = inject(AveragePickTimeDashboardService);
  private translate = inject(TranslateService);
  @Input() configuration!: AveragePickTimeDashboardConfiguration;

  intervalTime = 600000; // 10 minutes for analytics
  protected result: JobAveragePickTimeResponse | null = null;
  protected warnings: string[] = [];
  protected error: string | null = null;
  protected isLoading = false;
  protected dateFrom?: Dayjs;
  protected dateTo?: Dayjs;
  private interval?: ReturnType<typeof setInterval>;
  private isFirstLoad = true;

  protected canNavigateToJobs =
    this.authorizationService.hasPermission('job.read');

  private readonly defaultConfig: Partial<AveragePickTimeDashboardConfiguration> =
    {
      range: JobDashboardRange.TODAY,
      unitSourceType: UnitSourceType.ELEMENT_COUNT,
    };

  ngOnInit() {
    this.interval = setInterval(() => this.loadData(), this.intervalTime);
  }

  ngOnDestroy() {
    clearInterval(this.interval);
  }

  setConfiguration(configuration: unknown): void {
    this.configuration = {
      ...this.defaultConfig,
      ...this.configuration,
      ...(configuration as object),
    };
  }

  loadData() {
    if (!this.isConfigured()) {
      this.resetState();
      this.isLoading = false;
      return;
    }

    // Compute the date range used
    if (this.configuration.range === JobDashboardRange.CUSTOM) {
      this.dateFrom = this.configuration.from
        ? dayjs(this.configuration.from)
        : undefined;
      this.dateTo = this.configuration.to
        ? dayjs(this.configuration.to)
        : undefined;
    } else {
      const now = dayjs();
      this.dateFrom = this.service.getFromForRange(
        this.configuration.range,
        now,
      );
      this.dateTo = this.service.getToForRange(this.configuration.range, now);
    }

    // A custom range without both dates is treated like an unconfigured widget
    if (!this.dateFrom || !this.dateTo) {
      this.resetState();
      this.isLoading = false;
      return;
    }

    if (this.isFirstLoad) {
      this.isFirstLoad = false;
      this.isLoading = true;
    }
    this.service
      .getData(this.configuration, this.dateFrom, this.dateTo)
      .subscribe({
        next: (response) => {
          this.result = response;
          this.warnings = response.warnings;
          this.error = null;
          this.isLoading = false;
        },
        error: (err: HttpErrorResponse) => {
          const errorMessage = this.getErrorMessage(err);
          this.resetState();
          this.error = errorMessage;
          this.isLoading = false;
        },
      });
  }

  isConfigured(): boolean {
    if (this.configuration.unitSourceType === UnitSourceType.FIELD_SUM) {
      return !!this.configuration.unitFieldKey?.trim();
    }
    return true;
  }

  hasData(): boolean {
    return this.result?.averageSecondsPerUnit != null;
  }

  /** Average pick time formatted as seconds, or minutes and seconds above one minute. */
  formatAverage(): string {
    const seconds = this.result?.averageSecondsPerUnit;
    if (seconds == null) {
      return '–';
    }
    if (seconds < 60) {
      return `${this.formatNumber(seconds)} s`;
    }
    const minutes = Math.floor(seconds / 60);
    const remainder = seconds - minutes * 60;
    return `${minutes} min ${this.formatNumber(remainder)} s`;
  }

  formatNumber(value: number): string {
    return Number(value.toFixed(2)).toLocaleString();
  }

  getDateRangeDisplay(): string {
    if (!this.dateFrom || !this.dateTo) {
      return '';
    }
    const fromStr = this.dateFrom.format('DD.MM.YYYY');
    const toStr = this.dateTo.format('DD.MM.YYYY');
    return `${fromStr} - ${toStr}`;
  }

  isCustomRange(): boolean {
    return this.configuration.range === JobDashboardRange.CUSTOM;
  }

  getRangeKey(): string {
    // Convert enum value to key for translation lookup
    const entries = Object.entries(JobDashboardRange);
    const entry = entries.find(
      ([, value]) => value === this.configuration.range,
    );
    return entry ? entry[0] : 'TODAY';
  }

  private resetState(): void {
    this.result = null;
    this.warnings = [];
    this.error = null;
  }

  private getErrorMessage(err: HttpErrorResponse): string {
    // Handle specific error codes from the analytics endpoint
    if (err.status === 408) {
      return this.translate.instant('dashboard.averagePickTime.errors.timeout');
    }
    if (err.status === 400) {
      // Try to get the message from the response, fall back to translation
      const message = err.error?.message || err.error?.error;
      if (message) {
        return message;
      }
      return this.translate.instant(
        'dashboard.averagePickTime.errors.badRequest',
      );
    }
    // Generic error message
    return this.translate.instant('dashboard.averagePickTime.errors.generic');
  }
}
